/**
 * Patient Financial Panel — Ledger, Wallet, Payments summary card.
 * Rendered inside PatientProfile below the invoices section.
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Wallet, Receipt, TrendingUp, ArrowUpRight, ArrowDownRight,
  Plus, RotateCcw, Loader2, FileText, ChevronDown,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import CollectPaymentDialog from "./CollectPaymentDialog";

const INR = (n) => `₹${Number(n || 0).toLocaleString("en-IN", {
  minimumFractionDigits: 0, maximumFractionDigits: 2,
})}`;

const KIND_META = {
  invoice:        { label: "Invoice issued",   tone: "blue",    icon: FileText,       sign: "+" },
  invoice_void:   { label: "Invoice cancelled",tone: "muted",   icon: RotateCcw,      sign: "−" },
  payment:        { label: "Payment received", tone: "green",   icon: ArrowUpRight,   sign: "−" },
  wallet_credit:  { label: "Wallet credited",  tone: "wallet",  icon: Wallet,         sign: "+" },
  wallet_debit:   { label: "Wallet applied",   tone: "wallet",  icon: Wallet,         sign: "−" },
  refund:         { label: "Refund",           tone: "red",     icon: ArrowDownRight, sign: "−" },
  credit_note:    { label: "Credit note",      tone: "muted",   icon: FileText,       sign: "" },
  adjustment:     { label: "Adjustment",       tone: "muted",   icon: RotateCcw,      sign: "" },
};
const TONE = {
  blue:   "bg-blue-50 text-blue-800 border-blue-200",
  green:  "bg-emerald-50 text-emerald-800 border-emerald-200",
  red:    "bg-red-50 text-red-800 border-red-200",
  wallet: "bg-purple-50 text-purple-800 border-purple-200",
  muted:  "bg-linen text-ink-mid border-[#E8EAE9]",
};

export default function PatientFinancialPanel({ patient }) {
  const [ledger, setLedger] = useState({ entries: [], summary: {} });
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("ledger");
  const [payDialog, setPayDialog] = useState(false);
  const [expanded, setExpanded] = useState(false);   // collapsed by default
  const [showAll, setShowAll] = useState(false);     // cap long lists

  const load = useCallback(async () => {
    if (!patient?.id) return;
    setLoading(true);
    try {
      const [lg, pm, iv] = await Promise.all([
        api.get(`/patients/${patient.id}/ledger`),
        api.get(`/patients/${patient.id}/payments`),
        api.get(`/invoices?patient_id=${patient.id}`),
      ]);
      setLedger(lg.data);
      setPayments(pm.data);
      setInvoices(iv.data || []);
    } finally { setLoading(false); }
  }, [patient?.id]);

  useEffect(() => { load(); }, [load]);

  const s = ledger.summary || {};
  // Defensive belt-and-braces filter — even though the backend already scopes
  // by `patient_id`, we double-check every row on the client so nothing from
  // another patient can ever render inside this profile.
  const patientEntries = (ledger.entries || []).filter(
    (e) => e.patient_id === patient?.id,
  );
  const timeline = [...patientEntries].sort((a, b) => b.ts.localeCompare(a.ts));
  const ledgerAsc = [...patientEntries].sort((a, b) => a.ts.localeCompare(b.ts));
  // Invoice-number lookup for the "Linked Invoice" column.
  const invById = Object.fromEntries((invoices || []).map((i) => [i.id, i]));
  const outstandingInvoices = invoices.filter((i) =>
    ["pending", "partial"].includes(i.status));
  const refunds = timeline.filter((e) => e.kind === "refund");

  return (
    <div className="card-widget" data-testid="patient-financial-panel">
      {/* Collapsible header — always visible */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          data-testid="fin-toggle"
          className="flex items-center gap-3 text-left flex-1 min-w-0"
        >
          <ChevronDown className={`w-4 h-4 text-ink-soft shrink-0 transition-transform ${expanded ? "rotate-0" : "-rotate-90"}`} />
          <div className="min-w-0">
            <div className="overline text-ink-soft">Financial summary</div>
            <div className="font-display text-2xl text-ink">Ledger &amp; wallet</div>
          </div>
          <div className="hidden sm:flex items-center gap-2 ml-4 flex-wrap">
            <PillStat label="Outstanding" value={INR(s.outstanding || 0)}
                      tone={s.outstanding > 0 ? "amber" : "muted"} />
            <PillStat label="Wallet" value={INR(s.wallet_balance || 0)}
                      tone={s.wallet_balance > 0 ? "green" : "muted"} />
            {outstandingInvoices.length > 0 && (
              <PillStat label="Pending" value={outstandingInvoices.length} tone="amber" />
            )}
          </div>
        </button>
        <button
          type="button"
          onClick={() => setPayDialog(true)}
          data-testid="fin-collect-payment"
          className="text-xs inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-pine text-linen hover:bg-pine/90 shrink-0"
        >
          <Plus className="w-3.5 h-3.5" /> Collect payment
        </button>
      </div>

      {/* Mobile pills row (visible when hero pills row is hidden) */}
      <div className="sm:hidden mt-3 flex items-center gap-2 flex-wrap">
        <PillStat label="Outstanding" value={INR(s.outstanding || 0)}
                  tone={s.outstanding > 0 ? "amber" : "muted"} />
        <PillStat label="Wallet" value={INR(s.wallet_balance || 0)}
                  tone={s.wallet_balance > 0 ? "green" : "muted"} />
      </div>

      {/* Expandable body */}
      {expanded && (
        <div className="mt-4 space-y-4 animate-in fade-in slide-in-from-top-1">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <KPI label="Outstanding"
                 value={INR(s.outstanding || 0)}
                 tone={s.outstanding > 0 ? "amber" : "muted"}
                 icon={Receipt}
                 testid="fin-kpi-outstanding" />
            <KPI label="Wallet balance"
                 value={INR(s.wallet_balance || 0)}
                 tone={s.wallet_balance > 0 ? "green" : "muted"}
                 icon={Wallet}
                 testid="fin-kpi-wallet" />
            <KPI label="Total paid"
                 value={INR(s.total_paid || 0)}
                 icon={TrendingUp}
                 testid="fin-kpi-paid" />
            <KPI label="Total billed"
                 value={INR(s.total_billed || 0)}
                 icon={FileText}
                 testid="fin-kpi-billed" />
          </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-[#E8EAE9] overflow-x-auto no-scrollbar">
        {[
          { key: "ledger",       label: "Ledger",      count: timeline.length },
          { key: "invoices",     label: "Invoices",    count: invoices.length },
          { key: "payments",     label: "Payments",    count: payments.length },
          { key: "outstanding",  label: "Outstanding", count: outstandingInvoices.length, urgent: outstandingInvoices.length > 0 },
          { key: "wallet",       label: "Wallet",      count: null },
          { key: "refunds",      label: "Refunds",     count: refunds.length },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            data-testid={`fin-tab-${t.key}`}
            className={`px-3 py-2 text-xs border-b-2 transition whitespace-nowrap ${
              tab === t.key
                ? "border-pine text-ink font-medium"
                : "border-transparent text-ink-soft hover:text-ink"
            }`}
          >
            {t.label}
            {t.count != null && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] ${
                t.urgent && tab !== t.key
                  ? "bg-amber-100 text-amber-800"
                  : "bg-linen text-ink-mid"
              }`}>
                {t.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-8 text-center text-ink-soft">
          <Loader2 className="w-4 h-4 animate-spin inline" />
        </div>
      ) : tab === "ledger" ? (
        <LedgerTable entries={showAll ? ledgerAsc : ledgerAsc.slice(-10)} invById={invById} />
      ) : tab === "invoices" ? (
        <InvoicesList invoices={showAll ? invoices : invoices.slice(0, 10)} />
      ) : tab === "payments" ? (
        <PaymentsList payments={showAll ? payments : payments.slice(0, 10)} />
      ) : tab === "outstanding" ? (
        <OutstandingList
          invoices={showAll ? outstandingInvoices : outstandingInvoices.slice(0, 10)}
          onCollect={() => setPayDialog(true)} />
      ) : tab === "wallet" ? (
        <WalletHistory entries={(() => {
          const all = timeline.filter(
            (e) => e.kind === "wallet_credit" || e.kind === "wallet_debit");
          return showAll ? all : all.slice(0, 10);
        })()} />
      ) : (
        <RefundsList entries={showAll ? refunds : refunds.slice(0, 10)} />
      )}

      {/* Show all / show less toggle when the active tab has more than 10 rows */}
      {(() => {
        const walletAll = timeline.filter(
          (e) => e.kind === "wallet_credit" || e.kind === "wallet_debit");
        const counts = {
          ledger: timeline.length, invoices: invoices.length,
          payments: payments.length, outstanding: outstandingInvoices.length,
          wallet: walletAll.length, refunds: refunds.length,
        };
        if (counts[tab] <= 10) return null;
        return (
          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              data-testid="fin-show-all"
              className="text-xs text-ink-mid hover:text-ink px-4 py-1.5 rounded-full border border-pine/20 hover:bg-linen"
            >
              {showAll ? `Show 10 of ${counts[tab]}` : `Show all ${counts[tab]}`}
            </button>
          </div>
        );
      })()}
        </div>
      )}

      <CollectPaymentDialog
        open={payDialog}
        onOpenChange={setPayDialog}
        patientId={patient?.id}
        patientName={patient?.name}
        onSaved={() => load()}
      />
    </div>
  );
}


function PillStat({ label, value, tone = "pine" }) {
  const tones = {
    pine:  "bg-white border-[#E8EAE9] text-ink",
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    muted: "bg-linen border-[#E8EAE9] text-ink-mid",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border ${tones[tone]}`}>
      <span className="opacity-70 uppercase tracking-widest text-[9px]">{label}</span>
      <span className="font-mono">{value}</span>
    </span>
  );
}


function KPI({ label, value, tone = "pine", icon: Icon, testid }) {
  const tones = {
    pine:  "bg-white border-[#E8EAE9] text-ink",
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    muted: "bg-linen border-[#E8EAE9] text-ink-mid",
  };
  return (
    <div className={`rounded-2xl border px-3 py-2 ${tones[tone]}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70 flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="font-mono text-lg mt-1">{value}</div>
    </div>
  );
}

/* ---------- LedgerTable — proper accounting statement -------------------- */
/**
 * Debit vs Credit is defined from the patient's outstanding-balance view:
 *   Debit  = raises what the patient owes (invoice, refund paid to patient)
 *   Credit = lowers it (payment, wallet applied, invoice void, adjustment)
 * Wallet credit rows are shown for transparency but do not alter the
 * outstanding column — wallet is tracked separately in the summary card.
 */
const SIDE = {
  invoice:       "debit",
  refund:        "debit",
  payment:       "credit",
  wallet_debit:  "credit",
  invoice_void:  "credit",
  wallet_credit: "credit",
  adjustment:    "credit",
  credit_note:   "credit",
};

const TYPE_LABEL = {
  invoice:       "Invoice",
  invoice_void:  "Invoice void",
  payment:       "Payment",
  refund:        "Refund",
  wallet_credit: "Wallet credit",
  wallet_debit:  "Wallet applied",
  adjustment:    "Adjustment",
  credit_note:   "Credit note",
};

function LedgerTable({ entries, invById }) {
  if (entries.length === 0) {
    return (
      <div className="text-sm text-ink-soft text-center py-8" data-testid="fin-ledger-empty">
        No financial history yet.
      </div>
    );
  }
  const cellCls = "px-3 py-2.5 align-top";

  return (
    <div className="overflow-x-auto rounded-2xl border border-[#E8EAE9]" data-testid="fin-ledger-table">
      <table className="w-full text-sm min-w-[720px]">
        <thead className="bg-linen/60 text-[10px] uppercase tracking-widest text-ink-soft">
          <tr>
            <th className={`${cellCls} text-left`}>Date</th>
            <th className={`${cellCls} text-left`}>Type</th>
            <th className={`${cellCls} text-left`}>Description</th>
            <th className={`${cellCls} text-left`}>Invoice</th>
            <th className={`${cellCls} text-right`}>Debit</th>
            <th className={`${cellCls} text-right`}>Credit</th>
            <th className={`${cellCls} text-right`}>Balance</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#EEF0F2]">
          {entries.map((e) => {
            const meta = KIND_META[e.kind] || KIND_META.adjustment;
            const side = SIDE[e.kind] || "credit";
            const linkedInv = e.invoice_id && invById?.[e.invoice_id];
            const description =
                 e.note
              || (e.kind === "payment" && e.method ? `Payment · ${e.method}${e.ref ? ` · ${e.ref}` : ""}` : null)
              || (e.kind === "refund"  && e.method ? `Refund · ${e.method}${e.ref ? ` · ${e.ref}` : ""}` : null)
              || TYPE_LABEL[e.kind] || "—";
            return (
              <tr key={e.id} data-testid={`fin-ledger-row-${e.id}`} className="hover:bg-linen/40">
                <td className={`${cellCls} text-ink-mid whitespace-nowrap text-[12px]`}>
                  {new Date(e.ts).toLocaleString("en-IN", {
                    day: "2-digit", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit",
                  })}
                </td>
                <td className={cellCls}>
                  <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${TONE[meta.tone]}`}>
                    {TYPE_LABEL[e.kind] || e.kind}
                  </span>
                </td>
                <td className={`${cellCls} text-ink`}>
                  <div className="max-w-[260px] leading-snug">{description}</div>
                </td>
                <td className={`${cellCls} text-ink-mid text-[12px] font-mono`}>
                  {linkedInv ? linkedInv.invoice_number : "—"}
                </td>
                <td className={`${cellCls} text-right font-mono tabular-nums ${
                  side === "debit" ? "text-ink" : "text-ink-disabled"
                }`}>
                  {side === "debit" ? INR(e.amount) : "—"}
                </td>
                <td className={`${cellCls} text-right font-mono tabular-nums ${
                  side === "credit" ? "text-emerald-700" : "text-ink-disabled"
                }`}>
                  {side === "credit" ? INR(e.amount) : "—"}
                </td>
                <td className={`${cellCls} text-right font-mono tabular-nums font-semibold ${
                  e.running_outstanding > 0 ? "text-amber-900" : "text-ink"
                }`}>
                  {INR(e.running_outstanding || 0)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}




function LedgerTimeline({ entries }) {
  if (entries.length === 0) {
    return <div className="text-sm text-ink-soft text-center py-8">No financial history yet.</div>;
  }
  return (
    <div className="relative" data-testid="fin-ledger-timeline">
      <div className="absolute left-3 top-2 bottom-2 w-px bg-[#E8EAE9]" />
      <div className="space-y-3">
        {entries.map((e) => {
          const meta = KIND_META[e.kind] || KIND_META.adjustment;
          const Icon = meta.icon;
          return (
            <div key={e.id} className="relative pl-10 group">
              <div className={`absolute left-1 top-0 w-5 h-5 rounded-full border grid place-items-center ${TONE[meta.tone]}`}>
                <Icon className="w-2.5 h-2.5" />
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-ink font-medium">{meta.label}</span>
                    {e.method && e.kind === "payment" && (
                      <Badge variant="outline" className="text-[10px] border-pine/20 text-ink-mid">
                        {e.method}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-ink-soft">
                    {new Date(e.ts).toLocaleString("en-IN",
                      { dateStyle: "medium", timeStyle: "short" })}
                    {e.note && <> · {e.note}</>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono text-sm ${
                    ["payment","wallet_debit","invoice_void"].includes(e.kind)
                      ? "text-emerald-700"
                      : e.kind === "refund" ? "text-red-700"
                      : "text-ink"
                  }`}>
                    {meta.sign}{INR(e.amount)}
                  </div>
                  <div className="text-[10px] text-ink-soft">
                    Bal · {INR(e.running_outstanding)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function PaymentsList({ payments }) {
  if (payments.length === 0)
    return <div className="text-sm text-ink-soft text-center py-8">No payments recorded yet.</div>;
  return (
    <div className="space-y-2" data-testid="fin-payments-list">
      {payments.map((p) => (
        <div key={p.id} className="rounded-2xl border border-[#E8EAE9] p-3">
          <div className="flex items-center justify-between mb-1">
            <div className="text-sm font-medium text-ink">{INR(p.total)}</div>
            <div className="text-[11px] text-ink-soft">
              {new Date(p.ts).toLocaleString("en-IN",
                { dateStyle: "medium", timeStyle: "short" })}
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
            {(p.splits || []).map((s, i) => (
              <span key={i} className="px-2 py-0.5 rounded-full bg-linen text-ink/80">
                {s.method} · {INR(s.amount)}{s.ref ? ` · ${s.ref}` : ""}
              </span>
            ))}
            {p.remainder_to_wallet > 0 && (
              <span className="px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200 inline-flex items-center gap-0.5">
                <Wallet className="w-2.5 h-2.5" /> +{INR(p.remainder_to_wallet)} to wallet
              </span>
            )}
          </div>
          {p.note && <div className="text-[11px] text-ink-mid mt-1 italic">"{p.note}"</div>}
        </div>
      ))}
    </div>
  );
}


function WalletHistory({ entries }) {
  if (entries.length === 0)
    return <div className="text-sm text-ink-soft text-center py-8">No wallet activity yet.</div>;
  return (
    <div className="space-y-2" data-testid="fin-wallet-history">
      {entries.map((e) => {
        const isCredit = e.kind === "wallet_credit";
        return (
          <div key={e.id} className={`rounded-xl border px-3 py-2 flex items-center justify-between gap-2 ${
            isCredit
              ? "bg-purple-50/50 border-purple-100"
              : "bg-linen border-[#E8EAE9]"
          }`}>
            <div className="min-w-0">
              <div className="text-sm text-ink font-medium">
                {isCredit ? "Credit" : "Debit"}
              </div>
              <div className="text-[11px] text-ink-soft">
                {new Date(e.ts).toLocaleString("en-IN",
                  { dateStyle: "medium", timeStyle: "short" })}
                {e.note && <> · {e.note}</>}
              </div>
            </div>
            <div className={`font-mono text-sm ${isCredit ? "text-purple-700" : "text-ink-mid"}`}>
              {isCredit ? "+" : "−"}{INR(e.amount)}
            </div>
          </div>
        );
      })}
    </div>
  );
}


function InvoicesList({ invoices }) {
  if (invoices.length === 0)
    return <div className="text-sm text-ink-soft text-center py-8">No invoices yet.</div>;
  const STATUS_TONE = {
    paid:      "bg-emerald-50 border-emerald-200 text-emerald-800",
    partial:   "bg-amber-50 border-amber-200 text-amber-900",
    pending:   "bg-orange-50 border-orange-200 text-orange-900",
    cancelled: "bg-linen border-[#E8EAE9] text-ink-soft line-through",
  };
  return (
    <div className="space-y-2" data-testid="fin-invoices-list">
      {invoices.map((i) => (
        <div key={i.id} className="rounded-2xl border border-[#E8EAE9] p-3 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-sm text-ink">{i.invoice_number}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full border uppercase tracking-widest ${
                STATUS_TONE[i.status] || STATUS_TONE.pending}`}>
                {i.status}
              </span>
              {i.invoice_type === "gst" && (
                <Badge variant="outline" className="text-[10px] border-blue-200 bg-blue-50 text-blue-800">
                  GST
                </Badge>
              )}
            </div>
            <div className="text-[11px] text-ink-soft mt-0.5">
              {new Date(i.created_at).toLocaleDateString("en-IN")}
              {i.due_date && <> · due {new Date(i.due_date).toLocaleDateString("en-IN")}</>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-mono text-sm text-ink">{INR(i.total)}</div>
            {i.gst_amount > 0 && (
              <div className="text-[10px] text-ink-soft">incl. GST {INR(i.gst_amount)}</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}


function OutstandingList({ invoices, onCollect }) {
  if (invoices.length === 0)
    return <div className="text-sm text-ink-soft text-center py-8">🎉 Nothing outstanding.</div>;
  const total = invoices.reduce((s, i) => s + Number(i.total || 0), 0);
  return (
    <div className="space-y-3" data-testid="fin-outstanding-list">
      <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-3 flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-800">
            Balance due across {invoices.length} invoice{invoices.length === 1 ? "" : "s"}
          </div>
          <div className="font-mono text-2xl text-amber-900 mt-0.5">{INR(total)}</div>
        </div>
        <button
          type="button"
          onClick={onCollect}
          data-testid="fin-outstanding-collect"
          className="text-xs px-4 py-2 rounded-full bg-pine text-linen hover:bg-pine/90"
        >
          Collect payment
        </button>
      </div>
      <InvoicesList invoices={invoices} />
    </div>
  );
}


function RefundsList({ entries }) {
  if (entries.length === 0)
    return <div className="text-sm text-ink-soft text-center py-8">No refunds recorded.</div>;
  return (
    <div className="space-y-2" data-testid="fin-refunds-list">
      {entries.map((e) => (
        <div key={e.id} className="rounded-xl border border-red-100 bg-red-50/40 px-3 py-2 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-sm text-ink font-medium">Refund · {e.method || "—"}</div>
            <div className="text-[11px] text-ink-soft">
              {new Date(e.ts).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              {e.note && <> · {e.note}</>}
              {e.ref && <> · ref {e.ref}</>}
            </div>
          </div>
          <div className="font-mono text-sm text-red-700">−{INR(e.amount)}</div>
        </div>
      ))}
    </div>
  );
}
